/**
 * How much of the number survives.
 *
 * Named for the output, never for a caller: a style is a shape a figure can take,
 * and the moment one is called `ruler` or `axis` it can only be used there.
 *
 * - `exact`   — every digit the amount has, cents included: `$1,234.56`
 * - `coarse`  — fewest digits that still place it on a scale: `$120K`, `$2.5M`
 * - `compact` — one decimal at either scale: `$19.8K`, `$1.3M`
 * - `precise` — one more decimal where the scale is wide: `$12.5K`, `$1.70M`
 */
export type MoneyStyle = 'exact' | 'coarse' | 'compact' | 'precise'

/**
 * Decimal places per scale for each abbreviated style.
 *
 * `precise` earns its extra digit at the million scale from anything whose value
 * STEPS: a control that paints `value ± n × step` with a $10,000 step resolves
 * only $100,000 at one decimal, so ten consecutive positions print the same label
 * and the display reads as frozen while the value moves. The K scale needs no
 * extra digit — one decimal there already resolves $100, below the smallest step.
 */
const DECIMALS: Record<
  Exclude<MoneyStyle, 'exact'>,
  { millions: number; thousands: number }
> = {
  coarse: { millions: 1, thousands: 0 },
  compact: { millions: 1, thousands: 1 },
  precise: { millions: 2, thousands: 1 },
}

// A fraction of nothing but zeros is noise: "$2.00M" and "$12.0K" say no more
// than "$2M" and "$12K". A fraction with any non-zero digit stays whole, so
// "$1.70M" keeps its trailing zero and reads at the same precision as the
// "$1.71M" beside it.
const stripZeroFraction = (fixed: string): string => {
  const [whole, fraction] = fixed.split('.')
  return fraction != null && /[1-9]/.test(fraction) ? fixed : (whole ?? fixed)
}

/**
 * Abbreviates a MAGNITUDE to a K/M label.
 *
 * Takes a magnitude, not a signed amount: the scale thresholds are `>=`
 * comparisons, and a negative would fall past all of them into whole dollars.
 * The sign is applied by the caller, once, for every style.
 *
 * Implemented WITHOUT `Intl.NumberFormat`'s `notation: 'compact'` — Hermes (React
 * Native's engine) does not support it and silently falls back to standard
 * notation, so an Intl compact formatter looks correct in Node tests and is broken
 * on device.
 */
const abbreviate = (
  magnitude: number,
  style: Exclude<MoneyStyle, 'exact'>
): string => {
  const { millions, thousands } = DECIMALS[style]

  if (magnitude >= 1_000_000) {
    return `${stripZeroFraction((magnitude / 1_000_000).toFixed(millions))}M`
  }

  if (magnitude >= 1_000) {
    const label = stripZeroFraction((magnitude / 1_000).toFixed(thousands))
    // 999,950–999,999 rounds up to 1000K at any precision. Roll it up a scale so
    // it reads "1M" rather than "1000K".
    return Number(label) >= 1_000 ? '1M' : `${label}K`
  }

  return String(Math.round(magnitude))
}

/**
 * Formats an amount of money.
 *
 * One function for every shape money takes in this app, because they share the
 * one thing that must not vary: a negative goes in parentheses, never behind a
 * minus sign. That convention used to be hand-rolled per formatter, and the K/M
 * thresholds existed in three copies.
 *
 * There is no opt-out. A minus sign is wrong for both things money can be here:
 * a negative BALANCE is what parentheses are for, and a negative CHANGE wants a
 * signed pair (`+$14K` / `-$14K`) that a lone minus cannot give — so a change
 * passes its magnitude and lets an arrow carry the direction (`formatMoneyDelta`).
 *
 *   formatMoney(1234.56)                 // $1,234.56
 *   formatMoney(19_800, 'compact')       // $19.8K
 *   formatMoney(1_700_000, 'precise')    // $1.70M
 *   formatMoney(120_400, 'coarse')       // $120K
 *   formatMoney(-12_000, 'compact')      // ($12K)
 */
export const formatMoney = (
  amount: number,
  style: MoneyStyle = 'exact'
): string => {
  const magnitude = Math.abs(amount)

  const digits =
    style === 'exact'
      ? new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(magnitude)
      : abbreviate(magnitude, style)

  const formatted = `$${digits}`
  return amount < 0 ? `(${formatted})` : formatted
}
